import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';

const css = ".ispui-switcher{display:-ms-flexbox;display:flex}.ispui-switcher__button{position:relative;display:-ms-flexbox;display:flex;-ms-flex-align:center;align-items:center;-ms-flex-pack:center;justify-content:center;height:var(--ispui-switcher__button-height, 35px);padding:var(--ispui-switcher__button-padding, 0 10px);cursor:pointer;color:var(--isp-text-main);border:var(--ispui-switcher__button-border, 1px solid var(--isp-border-default));border-left:none;background:var(--ispui-switcher__button-bg, var(--isp-background-additional))}.ispui-switcher__button:hover{background:var(--ispui-switcher__button-bg-hover, transparent)}.ispui-switcher__button:focus{z-index:1}.ispui-switcher__button_disabled{pointer-events:none;opacity:0.5}.ispui-switcher__button_disabled:last-child.ispui-switcher__button_disabled{border-right:var(--ispui-switcher__button-border, 1px solid var(--isp-border-default));border-left:none}.ispui-switcher__button:first-child,.ispui-switcher__button_first{border-left:var(--ispui-switcher__button-border, 1px solid var(--isp-border-default));border-radius:var(--ispui-switcher__br, 3px) 0 0 var(--ispui-switcher__br, 3px)}.ispui-switcher__button:last-child,.ispui-switcher__button_last{border-radius:0 var(--ispui-switcher__br, 3px) var(--ispui-switcher__br, 3px) 0}.ispui-switcher__button_selected{cursor:default;background:var(--ispui-switcher__button-bg_selected, var(--isp-background-list))}.ispui-switcher__button_selected:hover{background:var(--ispui-switcher__button-bg_selected-hover, var(--isp-background-list))}";

const Switcher = /*@__PURE__*/ proxyCustomElement(class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.changeEvent = createEvent(this, "change", 7);
    // observing adding and removing options
    this.optionsObserver = new MutationObserver(mutations => {
      mutations
        .filter(mutation => mutation.type === 'childList')
        .forEach(mutation => {
        const isOptionAdded = mutation.addedNodes.length > 0;
        const isOptionRemoved = mutation.removedNodes.length === 1 && mutation.removedNodes[0].tagName === 'ISPUI-SWITCHER-OPTION';
        if (isOptionAdded || isOptionRemoved) {
          this.unsubscribe();
          this.initOptions();
        }
      });
    });
    this.value = '';
    this.multiple = false;
    this.disabled = false;
    this._value = [];
  }
  get optionNodeList() {
    return this.host.querySelectorAll('ispui-switcher-option');
  }
  get clickableOptionNodeList() {
    return this.host.querySelectorAll('ispui-switcher-option:not(.ispui-switcher__button_disabled)');
  }
  get firstSelectedOption() {
    return Array.from(this.optionNodeList).find(e => e.selected);
  }
  get firstClickableOption() {
    return Array.from(this.optionNodeList).find(e => !e.disabled);
  }
  connectedCallback() {
    this.initOptions();
    this.optionsObserver.observe(this.host, {
      childList: true,
    });
  }
  disconnectedCallback() {
    this.unsubscribe();
    this.optionsObserver.disconnect();
  }
  initOptions() {
    if (!this.optionNodeList) {
      return;
    }
    this.checkValidityOfComponent();
    this.clickEvent = this.clickOptionHandler.bind(this);
    this.optionNodeList.forEach(node => {
      node.addEventListener('click', this.clickEvent);
    });
    if (this.disabled) {
      this.setDisabled(this.disabled);
    }
    this.setValueInside(this.value);
    this.handleFocusability();
  }
  setDisabled(isDisabled) {
    this.optionNodeList.forEach(node => {
      node.disabled = isDisabled;
    });
  }
  resetSelected() {
    this.resetValue();
    this.optionNodeList.forEach(node => {
      node.selected = false;
    });
    this.handleFocusability();
    this.setValueOutside();
  }
  handleKeyDown(e) {
    var _a, _b, _c, _d;
    const options = Array.from(this.clickableOptionNodeList);
    if (!e.target) {
      return;
    }
    const target = e.target;
    const targetIndex = options.findIndex(ev => ev.skey === target.skey);
    switch (e.code) {
      case 'ArrowLeft':
        if (targetIndex > 0) {
          (_a = options[targetIndex - 1]) === null || _a === void 0 ? void 0 : _a.focus();
        }
        else {
          (_b = options[options.length - 1]) === null || _b === void 0 ? void 0 : _b.focus();
        }
        break;
      case 'ArrowRight':
        if (targetIndex < options.length - 1) {
          (_c = options[targetIndex + 1]) === null || _c === void 0 ? void 0 : _c.focus();
        }
        else {
          (_d = options[0]) === null || _d === void 0 ? void 0 : _d.focus();
        }
        break;
      case 'Space':
        e.preventDefault();
        target.click();
        break;
      case 'Enter':
        if (target.tagName === 'ISPUI-SWITCHER-OPTION') {
          e.preventDefault();
          target.click();
        }
        break;
    }
  }
  resetValue() {
    this.value = [];
    this._value = [];
  }
  handleFocusability() {
    if (!this.firstClickableOption) {
      return;
    }
    Array.from(this.optionNodeList).forEach(e => (e.focusable = false));
    if (this.firstSelectedOption) {
      this.firstSelectedOption.focusable = true;
    }
    else {
      this.firstClickableOption.focusable = true;
    }
  }
  checkValidityOfComponent() {
    const keys = Array.from(this.optionNodeList).map(e => e.skey);
    if (!this.checkUniqueness(keys)) {
      return console.error('skey в опциях ispui-switcher не должны повторяться.');
    }
  }
  checkUniqueness(arr) {
    return arr.length === new Set(arr).size;
  }
  setValueInside(value) {
    if (!value || value === this._value || value === this._value[0]) {
      return;
    }
    if (this.multiple) {
      if (Array.isArray(value)) {
        if (JSON.stringify(value.sort()) === JSON.stringify(this._value.sort())) {
          return;
        }
        value.forEach(v => {
          this.selectInMultipleMode(v);
        });
      }
      else if (value.length > 0 && Array.isArray(JSON.parse(value))) {
        if (JSON.stringify(JSON.parse(value).sort()) === JSON.stringify(this._value.sort())) {
          return;
        }
        JSON.parse(value).forEach(v => {
          this.selectInMultipleMode(v);
        });
      }
    }
    else {
      this.selectInSingleMode(value);
    }
  }
  setValueOutside() {
    if (this.multiple) {
      this.value = this._value;
    }
    else if (this._value && this._value[0]) {
      this.value = this._value[0];
    }
    this.changeEvent.emit({ value: this.value });
  }
  unsubscribe() {
    this.optionNodeList.forEach(option => {
      option.removeEventListener('click', this.clickEvent);
    });
  }
  clickOptionHandler(event) {
    const target = event.target;
    const option = target.closest('ispui-switcher-option');
    const skey = option.skey;
    if (this.multiple) {
      this.selectInMultipleMode(skey);
    }
    else {
      this.selectInSingleMode(skey);
    }
    this.handleFocusability();
    this.setValueOutside();
  }
  selectInSingleMode(skey) {
    this._value = [skey];
    this.optionNodeList.forEach(node => {
      if (node.skey !== skey) {
        node.selected = false;
      }
      else {
        node.selected = true;
      }
    });
  }
  selectInMultipleMode(skey) {
    this._value = [];
    this.optionNodeList.forEach(node => {
      if (node.skey == skey) {
        node.selected = !node.selected;
      }
    });
    this.optionNodeList.forEach(node => {
      if (node.selected) {
        this._value.push(node.skey);
      }
    });
  }
  render() {
    return (h(Host, { role: "listbox", class: "ispui-switcher" }, h("slot", null)));
  }
  get host() { return this; }
  static get watchers() { return {
    "disabled": ["setDisabled"],
    "multiple": ["resetSelected"],
    "value": ["setValueInside"]
  }; }
  static get style() { return css; }
}, [4, "ispui-switcher", {
    "value": [1025],
    "multiple": [4],
    "disabled": [4]
  }, [[0, "keydown", "handleKeyDown"]]]);
function defineCustomElement$1() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["ispui-switcher"];
  components.forEach(tagName => { switch (tagName) {
    case "ispui-switcher":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, Switcher);
      }
      break;
  } });
}

const IspuiSwitcher = Switcher;
const defineCustomElement = defineCustomElement$1;

export { IspuiSwitcher, defineCustomElement };
